import React, {useState} from "react"
import {NavigateFunction} from "react-router-dom"
import classNames from "classnames"
import Admonition from "../../Admonitions/Admonition"
import {BusinessProductApplication} from "../../../resources/productApplication"
import {Officer} from "../../../resources/application"
import CustomDropdown from "../../CustomDropdown/CustomDropdown"
import {BeneficialOwnerResource} from "../../../resources/beneficialOwner"
import {ConfirmationModal} from "../../ConfirmationModal/ConfirmationModal"
import {useEsc} from "../../../hooks/useEsc"

const officerContent = (officer: Officer) => {
	return <>{`Officer - ${officer.fullName.first} ${officer.fullName.last}`}</>
}

const boContent = (beneficialOwner: BeneficialOwnerResource) => {
	return (
		<>{`Beneficial Owner - ${beneficialOwner.attributes.fullName.first} ${beneficialOwner.attributes.fullName.last}`}</>
	)
}

const maxEntitiesBeforeExpandingDropdown = 2

export default function ReevaluateProductApplicationChooseEntityModal(props: {
	close: () => void
	onSuccess: () => void
	application: BusinessProductApplication
	officer?: Officer
	beneficialOwners?: BeneficialOwnerResource[]
	navigate: NavigateFunction
}) {
	const {close, application, navigate, officer, beneficialOwners} = props

	const modalTitle = "You are about to resubmit this business product application"
	const buttonText = "Edit Entity"
	const cancelButtonText = "Cancel"

	const warningMessage = (
		<p>
			You can choose one entity to edit and resubmit. Once that evaluation process ends you will be able to edit another
			entity.
		</p>
	)

	const businessEntityOption = {
		value: `/product-applications/${application.id}/resubmit`,
		key: `business`,
		content: <>Business</>,
	}

	const officerEntityOption = officer
		? {
				value: `/product-applications/${application.id}/officer/resubmit`,
				key: `officer`,
				content: officerContent(officer),
		  }
		: null

	const boEntitiesOption =
		beneficialOwners?.map((bo) => {
			return {
				value: `/product-applications/${application.id}/beneficialOwner/${bo.id}/resubmit`,
				key: `bo-${bo.id}`,
				content: boContent(bo),
			}
		}) ?? []

	const entitiesOptions = [
		businessEntityOption,
		...(officerEntityOption ? [officerEntityOption] : []),
		...boEntitiesOption,
	]

	const [navigationUrl, setNavigationUrl] = useState(businessEntityOption.value)

	useEsc(close)

	return (
		<ConfirmationModal
			close={close}
			onSubmit={() => {
				navigate(navigationUrl)
			}}
			okButtonText={buttonText}
			cancelButtonText={cancelButtonText}
			title={modalTitle}
			classname={"reevaluate-product-application-choose-entity-modal"}
			okButtonClassname={"button is-black mr-2"}
			cancelButtonClassname={"button is-white mr-2"}
		>
			<div className={"flex-container flex-dir-col gap-40"}>
				<label>Which entity would like to edit:</label>
				<CustomDropdown
					options={entitiesOptions}
					selectedValue={navigationUrl}
					onSelect={(val) => {
						setNavigationUrl(val)
					}}
					dropDownMenuClass={classNames(
						entitiesOptions.length > maxEntitiesBeforeExpandingDropdown
							? "reevaluate-application-choose-entity-modal"
							: "reevaluate-application-choose-entity-modal-multiple-selections"
					)}
				/>
				<Admonition type="is-warning" title="Attention" message={warningMessage} />
			</div>
		</ConfirmationModal>
	)
}
